import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, message, Space, Alert, Layout } from 'antd';
import { SaveOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useOutletContext } from 'react-router-dom';
import { settingsService, NumberingFormats } from '../../services/settings';

const { Text } = Typography;
const { Content } = Layout;
type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

export const NumberingFormatsPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [numberingData, setNumberingData] = useState<NumberingFormats | null>(null);
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();

  useEffect(() => {
    fetchNumberingFormats();
  }, []);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Numbering Formats
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const fetchNumberingFormats = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getNumberingFormats();
      setNumberingData(data);
      form.setFieldsValue(data);
    } catch (error) {
      message.error('Failed to load numbering formats');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: NumberingFormats) => {
    try {
      setLoading(true);
      await settingsService.updateNumberingFormats(values);
      message.success('Numbering formats updated successfully');
      setNumberingData(values);
    } catch (error) {
      message.error('Failed to update numbering formats');
    } finally {
      setLoading(false);
    }
  };

  const formatExamples = {
    'INV-{YYYY}-{MM}-{0000}': 'INV-2024-01-0001',
    'INV-{YY}{MM}{0000}': 'INV-2401001',
    'INV-{0000}': 'INV-0001',
    'PRD-{0000}': 'PRD-0001',
    'CUST-{0000}': 'CUST-0001',
    'SUP-{0000}': 'SUP-0001'
  };

  return (
    <Content style={{ padding: '24px' }}>
      <Text type="secondary">Configure automatic numbering formats for different modules</Text>
      
      <Alert
        message="Format Variables"
        description={
          <div>
            <p><strong>Available variables:</strong></p>
            <ul>
              <li><code>{'{YYYY}'}</code> - Full year (2024)</li>
              <li><code>{'{YY}'}</code> - Short year (24)</li>
              <li><code>{'{MM}'}</code> - Month (01-12)</li>
              <li><code>{'{DD}'}</code> - Day (01-31)</li>
              <li><code>{'{0000}'}</code> - Sequential number with padding (0001, 0002, etc.)</li>
            </ul>
          </div>
        }
        type="info"
        icon={<InfoCircleOutlined />}
        style={{ marginTop: '24px', marginBottom: '24px' }}
      />
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        style={{ marginTop: '24px' }}
      >
        <Card title="Invoice Numbering" style={{ marginBottom: '24px' }}>
          <Form.Item
            name="invoice_format"
            label="Invoice Number Format"
            rules={[{ required: true, message: 'Please enter invoice format' }]}
            extra="Example: INV-2024-01-0001"
          >
            <Input placeholder="INV-{YYYY}-{MM}-{0000}" />
          </Form.Item>
        </Card>

        <Card title="Product SKU Format" style={{ marginBottom: '24px' }}>
          <Form.Item
            name="product_sku_format"
            label="Product SKU Format"
            rules={[{ required: true, message: 'Please enter product SKU format' }]}
            extra="Example: PRD-0001"
          >
            <Input placeholder="PRD-{0000}" />
          </Form.Item>
        </Card>

        <Card title="Customer Code Format" style={{ marginBottom: '24px' }}>
          <Form.Item
            name="customer_code_format"
            label="Customer Code Format"
            rules={[{ required: true, message: 'Please enter customer code format' }]}
            extra="Example: CUST-0001"
          >
            <Input placeholder="CUST-{0000}" />
          </Form.Item>
        </Card>

        <Card title="Supplier Code Format" style={{ marginBottom: '24px' }}>
          <Form.Item
            name="supplier_code_format"
            label="Supplier Code Format"
            rules={[{ required: true, message: 'Please enter supplier code format' }]}
            extra="Example: SUP-0001"
          >
            <Input placeholder="SUP-{0000}" />
          </Form.Item>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
            Save Numbering Formats
          </Button>
          <Button onClick={() => form.resetFields()}>
            Reset
          </Button>
        </Space>
      </Form>
    </Content>
  );
};
